import React, {useEffect, useRef, useState} from 'react';
import {BoxProps} from '@material-ui/core/Box';
import {Typography} from '@material-ui/core';
import {Loader} from '@3plearning/react-components';
import styled from 'styled-components';

import {CenterBox} from '../layout/layout.styled';

type LoadingViewProps =
    & Omit<DelayedRendererProps, 'DelayedComponent'>
    & BoxProps
    & {
    DelayedComponent?: JSX.Element
    opacity?: number
    loadingText?: string
}

export const LoadingView = ({
    HiddenComponent,
    DelayedComponent,
    delayMs,
    loadingText,
    ...restProps
}: LoadingViewProps) => {
    DelayedComponent = DelayedComponent || <LoaderAnimationView/>;
    // placeholder used here to prevent any layout changes jerking the view
    HiddenComponent = HiddenComponent || <LoaderAnimationView opacity={0}/>;

    return (
        <CenterBox
            data-testid="loader"
            my={'20px'}
            spacing={'20px'}
            alignItems={'center'}
            {...restProps}
        >
            <LoadingSpinner
                DelayedComponent={DelayedComponent}
                HiddenComponent={HiddenComponent}
                delayMs={delayMs}
            />
            {loadingText ? <DelayedRenderer DelayedComponent={
                <Typography variant={'caption'}>
                    {loadingText}
                </Typography>
            }/> : null}
        </CenterBox>
    );
};

export const LoadingSpinner = ({ delayMs, DelayedComponent, HiddenComponent }: DelayedRendererProps) => {
    if (process.env.STORYSHOTS) {
        return null;
    }

    return (
        <DelayedRenderer
            delayMs={delayMs}
            DelayedComponent={DelayedComponent}
            HiddenComponent={HiddenComponent}
        />
    );
};

// Wrapper of react-components loader react-components/src/components/loader/index.js
// prevents styles passed down, so using a descendant selected used in this wrapper
export const LoaderAnimationWrapper = styled.div<{ opacity?: number }>`
  ${
    props => typeof props.opacity === 'number' && `
    opacity: ${props.opacity};
`}
`;

export const LoaderAnimationView = (props: React.PropsWithChildren<{ opacity?: number }>) => {
    return <LoaderAnimationWrapper {...props}>
        <Loader/>
    </LoaderAnimationWrapper>;
};

type DelayedRendererProps = {
    delayMs?: number
    HiddenComponent?: null | JSX.Element
    DelayedComponent: JSX.Element
}

const DelayedRenderer = ({
    HiddenComponent = null,
    DelayedComponent,
    delayMs = 1000
}: DelayedRendererProps) => {
    const timeoutId = useRef<number | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        // @ts-ignore // conflict with NodeJS.Timeout
        timeoutId.current = setTimeout(() => {
            setIsVisible(true);
        }, delayMs);

        return () => {
            if (timeoutId.current !== null) {
                clearTimeout(timeoutId.current);
            }
        };
    }, [delayMs]);

    return isVisible ? DelayedComponent : HiddenComponent;
};
